"use client";

/**
 * Demo mode for the landing-page editor takeover ("this page was written in
 * LessonFresh"). When this context is provided, a handful of components that
 * normally need an authenticated backend resolve their data locally instead:
 *
 *  - AuthImage renders a plain /public URL instead of an auth-fetched blob
 *  - EmbedFrame uses a fixed host allowlist instead of the course query
 *  - InteractiveEmbed reads activity data from the registry instead of the API
 *    (and skips completion/attempt beacons)
 *  - the interactive node view hides Edit (its modals fetch + save)
 *
 * Default is null, so production behavior is completely unchanged — the
 * provider is only mounted by the landing demo (components/demo/*).
 */

import { createContext, useContext } from "react";

export type DemoTask = "edit" | "drag" | "activity" | "student" | "chat";

export interface DemoInteractive {
  type:
    | "flashcards"
    | "matching"
    | "knowledge_check"
    | "sequencing"
    | "timeline"
    | "step_reveal"
    | "js_interactive";
  title: string;
  data: Record<string, unknown>;
}

export interface DemoData {
  /** assetId token → statically served image (under /public). */
  assets: Record<string, { src: string }>;
  /** interactiveId token → local activity payload (Playground pattern). */
  interactives: Record<string, DemoInteractive>;
  embedAllowedHosts: string[];
  /** Guided-checklist hook — components report first-time interactions. */
  markTask?: (task: DemoTask) => void;
}

export const DemoContext = createContext<DemoData | null>(null);

/** Null everywhere except inside the landing demo. */
export function useDemo(): DemoData | null {
  return useContext(DemoContext);
}
